import { formatFixed, tt } from '@agos/shared'

/**
 * Which supplier is cheapest for a stock item, and by how much.
 *
 * `GET /supplier-comparison/{stockItemId}` returns one row per supplier that has a
 * code mapped to the item, each carrying a last unit price and a six-month average.
 *
 * **The six-month average and its count are always zero.** FB-081:
 *
 * ```sql
 * (SELECT COALESCE(AVG(h.unit_price), 0)
 *    FROM supplier_item_price_history h
 *    JOIN invoices i2 ON i2.id = h.invoice_id          -- inner join
 *   WHERE h.supplier_item_code_id = c.id
 *     AND i2.issue_date >= CURRENT_DATE - INTERVAL '6 months')
 * ```
 *
 * Two things were wrong with it, and both are fixed on the server now (FB-081). The
 * join to `invoices` was **inner** while every price-history row has `invoice_id
 * NULL`, so it dropped all of them and the average came back 0 over a count of 0; and
 * the window was measured on the *invoice's* issue date rather than on the history
 * row's own `recorded_at`, so a price recorded without an invoice was invisible
 * whatever its date. It is a `LEFT JOIN` windowed on `COALESCE(i2.issue_date,
 * h.recorded_at::date)`.
 *
 * **This derivation stays, and not as a leftover.** The server's figure wins when it
 * has one — it knows about invoices this page never sees — and the fallback covers the
 * case a client cannot distinguish from the server's silence: no history in the window
 * at all. Derived figures still say they are derived, per FB-060.
 */

export interface ComparisonRow {
  supplierItemCodeId?: number
  supplierId?: number
  supplierName?: string
  supplierCode?: string
  unit?: string | null
  /** A per-unit purchase price. A rate, so it keeps all its decimals (FB-056). */
  lastUnitPrice?: number | null
  lastInvoiceId?: number | null
  lastInvoiceDate?: string | null
  lastInvoiceNumber?: string | null
  /** Always 0 — see FB-081. */
  avgPrice6mo?: number
  /** Always 0 — see FB-081. */
  count6mo?: number
}

export interface PricePoint {
  id?: number
  invoiceId?: number | null
  invoiceNumber?: string | null
  unitPrice?: number
  qty?: number | null
  recordedAt?: string
}

export interface StockItemOption {
  id?: number
  name?: string
  unit?: string
}

/** Six months back from a given instant, in milliseconds. */
export function sixMonthsBefore(now: number): number {
  const date = new Date(now)
  date.setMonth(date.getMonth() - 6)
  return date.getTime()
}

export interface DerivedAverage {
  /** The mean unit price over the window, or `null` when nothing falls in it. */
  averagePrice: number | null
  count: number
  /** Whether it came from the server or was worked out from the history here. */
  derived: boolean
}

/**
 * The six-month average, from the server if it has one and from the history if not.
 *
 * The server's figure wins when it is non-zero: it will know about invoices this page
 * never sees. A zero with a zero count is not an average of zero — it is the absence
 * of one, which is what FB-081 produces.
 */
export function sixMonthAverage(
  row: ComparisonRow,
  history: PricePoint[] | undefined,
  now: number,
): DerivedAverage {
  const reported = row.avgPrice6mo ?? 0
  const reportedCount = row.count6mo ?? 0
  if (reported > 0 && reportedCount > 0) {
    return { averagePrice: reported, count: reportedCount, derived: false }
  }

  const cutoff = sixMonthsBefore(now)
  const inWindow = (history ?? []).filter((point) => {
    const price = point.unitPrice
    if (price === undefined || !Number.isFinite(price) || price <= 0) return false
    const recordedAt = parseRecordedAt(point.recordedAt)
    return recordedAt !== null && recordedAt >= cutoff
  })

  if (inWindow.length === 0) return { averagePrice: null, count: 0, derived: true }

  const total = inWindow.reduce((sum, point) => sum + (point.unitPrice ?? 0), 0)
  return {
    averagePrice: round4(total / inWindow.length),
    count: inWindow.length,
    derived: true,
  }
}

/**
 * Postgres prints `2026-05-30 02:23:54.233302+00`.
 *
 * Two things `Date.parse` will not take: the space where ISO 8601 wants a `T`, and a
 * one-part offset — `+00` rather than `+00:00` or `Z`. Both are silently `NaN`, which
 * is how a whole price history reads as empty.
 */
export function parseRecordedAt(value: string | undefined): number | null {
  const text = (value ?? '').trim()
  if (text === '') return null

  const isoish = text
    .replace(' ', 'T')
    .replace(/([+-]\d{2})$/, '$1:00')

  const parsed = Date.parse(isoish)
  return Number.isFinite(parsed) ? parsed : null
}

/** A per-unit price is quoted to four decimals — 1,419 €/kg is a real supplier price. */
export function round4(value: number): number {
  return Math.round(value * 10_000) / 10_000
}

export interface Spread {
  cheapest: ComparisonRow
  dearest: ComparisonRow
  minPrice: number
  maxPrice: number
  /** How much more the dearest is, as a percentage of the cheapest. */
  percent: number
  /** How many suppliers actually have a price to compare. */
  priced: number
}

/**
 * The gap between the cheapest and the dearest supplier of one item.
 *
 * `null` when fewer than two suppliers have a price, because a spread of one supplier
 * against itself is not information. Legacy computes it from one supplier too and
 * shows 0 %, which reads as "they all charge the same".
 */
export function spreadOf(rows: ComparisonRow[]): Spread | null {
  const priced = rows.filter(
    (row) => row.lastUnitPrice != null && Number.isFinite(row.lastUnitPrice) && row.lastUnitPrice > 0,
  )
  if (priced.length < 2) return null

  const sorted = [...priced].sort(
    (left, right) => (left.lastUnitPrice ?? 0) - (right.lastUnitPrice ?? 0),
  )
  const cheapest = sorted[0]!
  const dearest = sorted.at(-1)!
  const minPrice = cheapest.lastUnitPrice ?? 0
  const maxPrice = dearest.lastUnitPrice ?? 0

  return {
    cheapest,
    dearest,
    minPrice,
    maxPrice,
    percent: minPrice > 0 ? round4(((maxPrice - minPrice) / minPrice) * 100) : 0,
    priced: priced.length,
  }
}

/** Cheapest first; a supplier with no price at all goes last. */
export function byPrice(rows: ComparisonRow[]): ComparisonRow[] {
  return [...rows].sort((left, right) => {
    const leftPrice = left.lastUnitPrice ?? Number.POSITIVE_INFINITY
    const rightPrice = right.lastUnitPrice ?? Number.POSITIVE_INFINITY
    if (leftPrice !== rightPrice) return leftPrice - rightPrice
    return (left.supplierName ?? '').localeCompare(right.supplierName ?? '', 'sk')
  })
}

export function isCheapest(row: ComparisonRow, rows: ComparisonRow[]): boolean {
  const prices = rows
    .map((candidate) => candidate.lastUnitPrice)
    .filter((price): price is number => price != null && price > 0)
  if (prices.length === 0) return false
  return row.lastUnitPrice === Math.min(...prices)
}

export type Trend = 'up' | 'down' | 'flat' | 'unknown'

/**
 * Where the last price sits against the average — which way it is moving.
 *
 * Deliberately against the **average** rather than the previous point: one cheap
 * delivery does not make a trend, and the average is the figure a buyer negotiates
 * with.
 */
export function trendOf(lastPrice: number | null | undefined, average: number | null): Trend {
  if (lastPrice == null || lastPrice <= 0 || average === null || average <= 0) return 'unknown'
  const difference = lastPrice - average
  // Under a tenth of a cent is noise on a per-unit price, not a direction.
  if (Math.abs(difference) < 0.001) return 'flat'
  return difference > 0 ? 'up' : 'down'
}

/**
 * `1,4190 €` — four decimals, because a supplier quotes them.
 *
 * Fixed width, not a maximum: dropping the trailing zero drops the precision the supplier stated. §9.3
 * moved the formatting itself into `@agos/shared`, which also puts the thousands separator back — this was
 * `toFixed(4).replace('.', ',')`, right about the comma and silent about the space.
 */
export function formatUnitPrice(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value === 0) return '-'
  return `${formatFixed(value, 4)} €`
}

export function formatPercent(value: number): string {
  const rounded = Math.round(value * 10) / 10
  return `${String(rounded).replace('.', ',')} %`
}

/**
 * The items worth offering in the picker.
 *
 * Legacy slices the first 50 of whatever order the API returned, so with 135 items the
 * unsearched list is an arbitrary third of them. Sorted by name, so the same 50 are the
 * first 50 alphabetically and the picker is at least predictable.
 */
export function pickableItems(items: StockItemOption[], search: string, limit = 50): StockItemOption[] {
  const needle = search.trim().toLowerCase()
  return items
    .filter((item) => needle === '' || (item.name ?? '').toLowerCase().includes(needle))
    .sort((left, right) => (left.name ?? '').localeCompare(right.name ?? '', 'sk'))
    .slice(0, limit)
}

/** Said out loud when the picker is showing less than it has. */
export function truncationNote(items: StockItemOption[], search: string, limit = 50): string | null {
  const matching = pickableItems(items, search, Number.POSITIVE_INFINITY).length
  if (matching <= limit) return null
  return tt('Zobrazených je prvých 50 - zúžte hľadanie.')
}
